// Deterministic amount-to-words for contract sums. Exactness matters for a legal
// figure, so this is computed, not generated. Produces cheque-style wording, e.g.
// "Forty-Six Million, Eight Hundred Thirty Thousand, Three Hundred Forty-Four
//  United States Dollars".

import type { Money } from "./types";

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const SCALES = ["", "Thousand", "Million", "Billion", "Trillion", "Quadrillion"];

// Currency code -> spoken unit names.
const CCY: Record<string, { one: string; many: string; cent: string; centMany: string }> = {
  USD: { one: "United States Dollar", many: "United States Dollars", cent: "Cent", centMany: "Cents" },
  GYD: { one: "Guyanese Dollar", many: "Guyanese Dollars", cent: "Cent", centMany: "Cents" },
  EUR: { one: "Euro", many: "Euros", cent: "Cent", centMany: "Cents" },
  GBP: { one: "Pound Sterling", many: "Pounds Sterling", cent: "Penny", centMany: "Pence" },
  CAD: { one: "Canadian Dollar", many: "Canadian Dollars", cent: "Cent", centMany: "Cents" },
  TTD: { one: "Trinidad and Tobago Dollar", many: "Trinidad and Tobago Dollars", cent: "Cent", centMany: "Cents" },
  XCD: { one: "East Caribbean Dollar", many: "East Caribbean Dollars", cent: "Cent", centMany: "Cents" },
  JMD: { one: "Jamaican Dollar", many: "Jamaican Dollars", cent: "Cent", centMany: "Cents" },
  CNY: { one: "Renminbi Yuan", many: "Renminbi Yuan", cent: "Fen", centMany: "Fen" },
};

function threeToWords(n: number): string {
  const out: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) out.push(`${ONES[hundreds]} Hundred`);
  if (rest) {
    if (rest < 20) out.push(ONES[rest]);
    else {
      const t = Math.floor(rest / 10);
      const o = rest % 10;
      out.push(o ? `${TENS[t]}-${ONES[o]}` : TENS[t]);
    }
  }
  return out.join(" ");
}

export function integerToWords(num: number): string {
  if (!isFinite(num)) return "";
  num = Math.floor(Math.abs(num));
  if (num === 0) return "Zero";
  const groups: number[] = [];
  while (num > 0) {
    groups.push(num % 1000);
    num = Math.floor(num / 1000);
  }
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue;
    const words = threeToWords(groups[i]);
    parts.push(SCALES[i] ? `${words} ${SCALES[i]}` : words);
  }
  return parts.join(", ");
}

/** Full contract-sum wording including currency and any cents. */
export function amountToWords(m?: Money): string {
  if (!m || m.amount == null || !isFinite(m.amount)) return "";
  const code = (m.currency || "").toUpperCase();
  const names = CCY[code] || { one: m.currency || "unit", many: m.currency || "units", cent: "Cent", centMany: "Cents" };
  const whole = Math.floor(Math.abs(m.amount));
  const cents = Math.round((Math.abs(m.amount) - whole) * 100);
  const wholeWords = `${integerToWords(whole)} ${whole === 1 ? names.one : names.many}`;
  if (cents > 0) {
    return `${wholeWords} and ${integerToWords(cents)} ${cents === 1 ? names.cent : names.centMany}`;
  }
  return wholeWords;
}
